import { useEffect, useRef } from "react";
import { useTooltipGroup } from "./useTooltipGroup";
import {
  getTooltipPrefs,
  setTooltipPrefs,
  useGlobalTooltipPrefs,
  useTooltipPrefsHydrated,
  useTooltipPrefsVersion,
} from "./prefs";

import { initHoverTips, runTour, stopHoverTips } from "./engine";
import "./tooltips.css";

/**
 * Drop-in tooltip runtime for a page/feature. Runs the admin-authored
 * walkthrough on first visit, then wires the hover/focus tips.
 *
 * Admin group toggles are the ceiling; the user preference can only opt out.
 * Hover tips and the guided tour are opted out independently.
 */
export function TooltipGroup({
  groupKey,
  active = true,
  delayMs = 600,
}: {
  groupKey: string;
  /** Set false while the page/tab that owns the anchors is not visible. */
  active?: boolean;
  delayMs?: number;
}) {
  const { data } = useTooltipGroup(groupKey);
  const global = useGlobalTooltipPrefs();
  // Re-evaluate whenever the user flips a toggle or resets from the profile menu.
  const prefsVersion = useTooltipPrefsVersion();
  // Stored prefs may say this walkthrough is already done on another device.
  const hydrated = useTooltipPrefsHydrated();
  const started = useRef(false);

  useEffect(() => {
    const prefs = getTooltipPrefs(groupKey);
    const canTour = Boolean(data?.group.tour_enabled) && prefs.tour && global.tour;
    const canTip = Boolean(data?.group.tips_enabled) && prefs.tips && global.tips;

    // A reset re-arms the walkthrough, so let it run again.
    if (prefs.tour) started.current = false;
    if (!active || !hydrated || !data?.group.is_enabled) {
      stopHoverTips();
      return;
    }
    if (started.current) return;
    started.current = true;


    const tips = data.tips;

    if (canTour) {
      const t = window.setTimeout(
        () =>
          runTour(tips, {
            onFinish: () => {
              setTooltipPrefs(groupKey, { tour: false });
              if (canTip) initHoverTips(tips);
            },
          }),
        delayMs,
      );
      return () => window.clearTimeout(t);
    }

    if (canTip) initHoverTips(tips);
    else stopHoverTips();
  }, [active, data, groupKey, delayMs, global.tips, global.tour, prefsVersion, hydrated]);

  useEffect(() => () => stopHoverTips(), []);

  return null;
}

export default TooltipGroup;
